import { commonValidationMessages } from "Components/ValidationErrorMessages";
import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import {
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Col,
  Form,
  Input,
  Label,
  Row,
} from "reactstrap";
import { useFormik } from "formik";
import FormValuesDebug from "utils/debugTools/FormValuesDebug";
import apiGlobal from "global/api.global";
import Loading from "Components/Loading";
import { checkValidArray, checkValidObject, handleServerResponse, saveEditForm, useSafeFormikContext } from "GenericForms/Helper";
import ErrorTooltip from "Components/ErrorTooltip";
import env from "environment_system/env_system";
import DeletePopOver from "Components/DeletePopOver";
import { successToast } from "Components/Toasts";
import { RootState } from "index";
import { useDispatch, useSelector } from "react-redux";
import { getCurrentReportAction } from "Store/Generic/ReportingSlice";
import ErrorComponent from "Components/ErrorComponent";
import { useEditReport } from "VesselReportingInfo/EditCompletedReports/EditReportProvider";

interface ThermalOilHeaterComponentType {
  toggleAddTab: any;
  toggleDynamicTabs: any;
  setErrorMessage: (value: boolean) => void;
  editReportBool: boolean;
  setPreviousTabIndex: any;
  activeAddTab: number;
}

const ThermalOilHeaterComponent = ({
  toggleAddTab,
  toggleDynamicTabs,
  setErrorMessage,
  editReportBool,
  setPreviousTabIndex,
  activeAddTab
}: ThermalOilHeaterComponentType) => {
  /** State variables start */
  const { previousEnggTabIndex, discardwarning } = useEditReport();
  const { VesselID, VoyageID, ReportID, CurrentReport, reportingLoading, reportingError } = useSelector((state: RootState) => state.Reporting);
  const dispatch = useDispatch();
  const thermalOilHeaterCurrentReport = CurrentReport?.data?.chief_engineer?.lfonds_thermal_oil_heater ?? null;
  const [refreshKey, setRefreshKey] = useState(0);
  const [popOverBool, setPopOverBool] = useState<boolean | null>(false);
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);
  const [thermalOilHeaterObj, setThermalOilHeaterObj] = useState<any>({});
  const formikContext = useSafeFormikContext<any>();
  const editValues = formikContext?.values;
  const setEditValues = formikContext?.setValues;
  const editInitialValues = formikContext?.initialValues;
  const editSubmit = formikContext?.submitForm;
  const editIsSubmitting = formikContext?.isSubmitting;
  /** State variables end */

  /** Assign values to initial object of Thermal Oil Heater */
  const getInitialThermalOilHeaterValues = () => {
    if (checkValidArray(editInitialValues?.chief_engineer?.lfonds_thermal_oil_heater)) {
      return editInitialValues?.chief_engineer?.lfonds_thermal_oil_heater;
    } else if (checkValidArray(thermalOilHeaterCurrentReport)) {
      return thermalOilHeaterCurrentReport;
    } else {
      return [
        {
          name_of_thermal_oil_heater: "",
          outlet_temperature: 0,
          inlet_temperature: 0,
          exhaust_temperature: 0,
          outlet_press: 0,
          inlet_press: 0,
          temperature_set_point: 0,
          th_expansion_tank_level: 0,
          vessel: VesselID,
          voyage_information: VoyageID,
          vessel_reporting_information: ReportID,
        },
      ];
    }
  };

  /** Thermal oil heater's formik object */
  const ThermalOilHeaterFormik: any = useFormik({
    enableReinitialize: true,
    initialValues: {
      thermalOilHeater: getInitialThermalOilHeaterValues(),
    },
    validationSchema: Yup.object().shape({
      thermalOilHeater: Yup.array(
        Yup.object({
          name_of_thermal_oil_heater: Yup.string()
            .trim()
            .min(2, commonValidationMessages.min2)
            .max(100, commonValidationMessages.max100Char),
          outlet_temperature: Yup.string().matches(
            /^\d{0,3}(\.\d{1,4})?$/,
            `${commonValidationMessages.before3after4}`
          ),
          inlet_temperature: Yup.string().matches(
            /^\d{0,3}(\.\d{1,4})?$/,
            `${commonValidationMessages.before3after4}`
          ),
          exhaust_temperature: Yup.string().matches(
            /^\d{0,3}(\.\d{1,4})?$/,
            `${commonValidationMessages.before3after4}`
          ),
          outlet_press: Yup.string().matches(
            /^\d{0,3}(?:\.\d{1,4})?$/,
            `${commonValidationMessages.before3after4}`
          ),
          inlet_press: Yup.string().matches(
            /^\d{0,3}(?:\.\d{1,4})?$/,
            `${commonValidationMessages.before3after4}`
          ),
          temperature_set_point: Yup.string().matches(
            /^\d{0,4}(\.\d{1,4})?$/,
            `${commonValidationMessages.before4after4}`
          ),
          th_expansion_tank_level: Yup.string().matches(
            /^\d{0,4}(?:\.\d{1,4})?$/,
            `${commonValidationMessages.before4after4}`
          ),
        })
      ),
    }),
    onSubmit: async (values: any, actions: any) => {
      if (env?.form_validation === false) {
        setErrorMessage(null)
        toggleDynamicTabs(previousEnggTabIndex + 1);
      }
      actions.setSubmitting(true);
      let responseArray: any = [];
      if (checkValidArray(thermalOilHeaterCurrentReport)) {
        values.thermalOilHeater.forEach((heater: any) => {
          if (heater.id) {
            responseArray.push(apiGlobal.put(`/lfonds_thermal_oil_heater/${heater.id}/`, heater));
          } else {
            responseArray.push(apiGlobal.post(`/lfonds_thermal_oil_heater/`, heater));
          }
        });
      } else {
        responseArray.push(apiGlobal.post(`/lfonds_thermal_oil_heater/`, values.thermalOilHeater));
      }
      await handleServerResponse(responseArray).then(async (res: any) => {
        if (res === true) {
          dispatch(getCurrentReportAction(VesselID, ReportID));
          setRefreshKey(refreshKey + 1);
          if (env?.form_validation === true) {
            setErrorMessage(null)
            toggleDynamicTabs(previousEnggTabIndex + 1);
          }
        } else {
          setErrorMessage(res)
        }
        actions.setSubmitting(false);
      })
    }
  });

  /** useEffect */
  useEffect(() => {
    ThermalOilHeaterFormik.initialValues.thermalOilHeater = getInitialThermalOilHeaterValues();
    setRefreshKey(refreshKey + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thermalOilHeaterCurrentReport, editInitialValues]);
  useEffect(() => {
    if (editValues?.chief_engineer) {
      setEditValues({
        ...editValues,
        chief_engineer: {
          ...editValues?.chief_engineer,
          lfonds_thermal_oil_heater: ThermalOilHeaterFormik?.values?.thermalOilHeater,
        }
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ThermalOilHeaterFormik?.values?.thermalOilHeater])
  /** useEffect end */

  /**
   *  Deletes the thermal oil heater object from the database
   * @param indexId 
   */
  const handleDeletion = async (indexId: number) => {
    try {
      if (checkValidObject(thermalOilHeaterObj) && thermalOilHeaterObj.id && !editReportBool) {
        await apiGlobal.delete(`/lfonds_thermal_oil_heater/${thermalOilHeaterObj.id}/`, thermalOilHeaterObj);
        successToast('Deleted successfully');
        dispatch(getCurrentReportAction(VesselID, ReportID));
        setRefreshKey(refreshKey + 1);
      } else {
        let data = [...ThermalOilHeaterFormik?.values?.thermalOilHeater];
        data.splice(indexId, 1);
        ThermalOilHeaterFormik?.setValues({
          thermalOilHeater: data,
        })
        setRefreshKey(refreshKey + 1);
      }
    } catch (err) {
      console.error('Error:', err);
    }
  };

  return (
    <Card className="p-0 mb-0 border-0">
      <CardHeader className="p-2">
        <div className="text-center mb-2">
          <Row>
            <Col>
              <h4 className="page_title pos-start mb-0">Thermal Oil Heaters</h4>
              <p className="card-title-desc pos-start">
                All readings since last report
              </p>
            </Col>
          </Row>
        </div>
      </CardHeader>
      {reportingLoading && <Loading message="Loading required data!" />}
      {reportingError && <ErrorComponent message="Error loading required data!" />}
      {!reportingLoading && !reportingError && (
        <Form autoComplete="off" onSubmit={ThermalOilHeaterFormik?.handleSubmit} noValidate key={refreshKey}>
          <CardBody className="engineer-card-body">
            {ThermalOilHeaterFormik?.values &&
              ThermalOilHeaterFormik?.values?.thermalOilHeater?.map(
                (heater: any, index: number) => {
                  return (
                    <React.Fragment key={index}>
                      <Row>
                        <Col>
                          {ThermalOilHeaterFormik?.values?.thermalOilHeater?.length === 1 ? null : (
                            <button
                              type="button"
                              className="btn justify_right"
                            >
                              <i
                                className="dripicons-trash icon_s18"
                                id={`delete_${index}`}
                                onClick={() => {
                                  setThermalOilHeaterObj(heater);
                                  setDeleteIndex(index);
                                  setPopOverBool(true);
                                }}
                              />
                            </button>
                          )}
                        </Col>
                      </Row>
                      <Row className="mb-2">
                        <Col lg={3}>
                          <Label
                            className="mb-0"
                            for={`name_of_thermal_oil_heater-${index}`}
                          >
                            Equipment name
                          </Label>
                          <Input
                            onFocus={(e) => e.target.select()}
                            name={`thermalOilHeater.${index}.name_of_thermal_oil_heater`}
                            type="text"
                            className="form-control"
                            id={`name_of_thermal_oil_heater-${index}`}
                            onBlur={(e: any) => ThermalOilHeaterFormik?.handleChange(e)}
                            defaultValue={ThermalOilHeaterFormik?.values?.thermalOilHeater[index]?.name_of_thermal_oil_heater}
                          />
                          {ThermalOilHeaterFormik?.errors?.thermalOilHeater &&
                            ThermalOilHeaterFormik?.errors?.thermalOilHeater[index]
                              ?.name_of_thermal_oil_heater &&
                            env?.form_validation === true && !discardwarning && (
                              <ErrorTooltip
                                target={`name_of_thermal_oil_heater-${index}`}
                                message={
                                  ThermalOilHeaterFormik?.errors?.thermalOilHeater[index]
                                    ?.name_of_thermal_oil_heater
                                }
                                open={
                                  ThermalOilHeaterFormik?.errors?.thermalOilHeater &&
                                    ThermalOilHeaterFormik?.errors?.thermalOilHeater[index]
                                      ?.name_of_thermal_oil_heater
                                    ? true
                                    : false
                                }
                              />
                            )}
                        </Col>
                        <Col lg={3}>
                          <Label
                            className="mb-0"
                            for={`outlet_temperature-${index}`}
                          >
                            Outlet temperature
                          </Label>
                          <div className="input-group">
                            <Input
                              onFocus={(e) => e.target.select()}
                              name={`thermalOilHeater.${index}.outlet_temperature`}
                              type="text"
                              className="form-control text-right max-width-7"
                              id={`outlet_temperature-${index}`}
                              onBlur={(e: any) => ThermalOilHeaterFormik?.handleChange(e)}
                              defaultValue={ThermalOilHeaterFormik?.values?.thermalOilHeater[index]?.outlet_temperature}
                            />
                            <div className="input-group-text round_border">
                              <sup>o</sup>C
                            </div>
                          </div>
                          {ThermalOilHeaterFormik?.errors?.thermalOilHeater &&
                            ThermalOilHeaterFormik?.errors?.thermalOilHeater[index]
                              ?.outlet_temperature &&
                            env?.form_validation === true && !discardwarning && (
                              <ErrorTooltip
                                target={`outlet_temperature-${index}`}
                                message={
                                  ThermalOilHeaterFormik?.errors?.thermalOilHeater[index]
                                    ?.outlet_temperature
                                }
                                open={
                                  ThermalOilHeaterFormik?.errors?.thermalOilHeater &&
                                    ThermalOilHeaterFormik?.errors?.thermalOilHeater[index]
                                      ?.outlet_temperature
                                    ? true
                                    : false
                                }
                              />
                            )}
                        </Col>
                        <Col lg={3}>
                          <Label
                            className="mb-0"
                            for={`inlet_temperature-${index}`}
                          >
                            Inlet temperature
                          </Label>
                          <div className="input-group">
                            <Input
                              onFocus={(e) => e.target.select()}
                              name={`thermalOilHeater.${index}.inlet_temperature`}
                              type="text"
                              className="form-control text-right max-width-7"
                              id={`inlet_temperature-${index}`}
                              onBlur={(e: any) => ThermalOilHeaterFormik?.handleChange(e)}
                              defaultValue={ThermalOilHeaterFormik?.values?.thermalOilHeater[index]?.inlet_temperature}
                            />
                            <div className="input-group-text round_border">
                              <sup>o</sup>C
                            </div>
                          </div>
                          {ThermalOilHeaterFormik?.errors?.thermalOilHeater &&
                            ThermalOilHeaterFormik?.errors?.thermalOilHeater[index]
                              ?.inlet_temperature &&
                            env?.form_validation === true && !discardwarning && (
                              <ErrorTooltip
                                target={`inlet_temperature-${index}`}
                                message={
                                  ThermalOilHeaterFormik?.errors?.thermalOilHeater[index]
                                    ?.inlet_temperature
                                }
                                open={
                                  ThermalOilHeaterFormik?.errors?.thermalOilHeater &&
                                    ThermalOilHeaterFormik?.errors?.thermalOilHeater[index]
                                      ?.inlet_temperature
                                    ? true
                                    : false
                                }
                              />
                            )}
                        </Col>
                        <Col lg={3}>
                          <Label
                            className="mb-0"
                            for={`exhaust_temperature-${index}`}
                          >
                            Exhaust temperature
                          </Label>
                          <div className="input-group">
                            <Input
                              onFocus={(e) => e.target.select()}
                              name={`thermalOilHeater.${index}.exhaust_temperature`}
                              type="text"
                              className="form-control text-right max-width-7"
                              id={`exhaust_temperature-${index}`}
                              onBlur={(e: any) => ThermalOilHeaterFormik?.handleChange(e)}
                              defaultValue={ThermalOilHeaterFormik?.values?.thermalOilHeater[index]?.exhaust_temperature}
                            />
                            <div className="input-group-text round_border">
                              <sup>o</sup>C
                            </div>
                          </div>
                          {ThermalOilHeaterFormik?.errors?.thermalOilHeater &&
                            ThermalOilHeaterFormik?.errors?.thermalOilHeater[index]
                              ?.exhaust_temperature &&
                            env?.form_validation === true && !discardwarning && (
                              <ErrorTooltip
                                target={`exhaust_temperature-${index}`}
                                message={
                                  ThermalOilHeaterFormik?.errors?.thermalOilHeater[index]
                                    ?.exhaust_temperature
                                }
                                open={
                                  ThermalOilHeaterFormik?.errors?.thermalOilHeater &&
                                    ThermalOilHeaterFormik?.errors?.thermalOilHeater[index]
                                      ?.exhaust_temperature
                                    ? true
                                    : false
                                }
                              />
                            )}
                        </Col>
                      </Row>
                      <Row className="mb-3">
                        <Col lg={3}>
                          <Label
                            className="mb-0"
                            for={`outlet_press-${index}`}
                          >
                            Outlet press
                          </Label>
                          <Input
                            onFocus={(e) => e.target.select()}
                            name={`thermalOilHeater.${index}.outlet_press`}
                            type="text"
                            className="form-control text-right max-width-7"
                            id={`outlet_press-${index}`}
                            onBlur={(e: any) => ThermalOilHeaterFormik?.handleChange(e)}
                            defaultValue={ThermalOilHeaterFormik?.values?.thermalOilHeater[index]?.outlet_press}
                          />
                          {ThermalOilHeaterFormik?.errors?.thermalOilHeater &&
                            ThermalOilHeaterFormik?.errors?.thermalOilHeater[index]
                              ?.outlet_press &&
                            env?.form_validation === true && !discardwarning && (
                              <ErrorTooltip
                                target={`outlet_press-${index}`}
                                message={
                                  ThermalOilHeaterFormik?.errors?.thermalOilHeater[index]
                                    ?.outlet_press
                                }
                                open={
                                  ThermalOilHeaterFormik?.errors?.thermalOilHeater &&
                                    ThermalOilHeaterFormik?.errors?.thermalOilHeater[index]
                                      ?.outlet_press
                                    ? true
                                    : false
                                }
                              />
                            )}
                        </Col>
                        <Col lg={3}>
                          <Label
                            className="mb-0"
                            for={`inlet_press-${index}`}
                          >
                            Inlet press
                          </Label>
                          <Input
                            onFocus={(e) => e.target.select()}
                            name={`thermalOilHeater.${index}.inlet_press`}
                            type="text"
                            className="form-control text-right max-width-7"
                            id={`inlet_press-${index}`}
                            onBlur={(e: any) => ThermalOilHeaterFormik?.handleChange(e)}
                            defaultValue={ThermalOilHeaterFormik?.values?.thermalOilHeater[index]?.inlet_press}
                          />
                          {ThermalOilHeaterFormik?.errors?.thermalOilHeater &&
                            ThermalOilHeaterFormik?.errors?.thermalOilHeater[index]
                              ?.inlet_press &&
                            env?.form_validation === true && !discardwarning && (
                              <ErrorTooltip
                                target={`inlet_press-${index}`}
                                message={
                                  ThermalOilHeaterFormik?.errors?.thermalOilHeater[index]
                                    ?.inlet_press
                                }
                                open={
                                  ThermalOilHeaterFormik?.errors?.thermalOilHeater &&
                                    ThermalOilHeaterFormik?.errors?.thermalOilHeater[index]
                                      ?.inlet_press
                                    ? true
                                    : false
                                }
                              />
                            )}
                        </Col>
                        <Col lg={3}>
                          <Label
                            className="mb-0"
                            for={`thermalOilHeater.${index}.temperature_set_point`}
                          >
                            Temperature set point
                          </Label>
                          <div className="input-group">
                            <Input
                              onFocus={(e) => e.target.select()}
                              name={`thermalOilHeater.${index}.temperature_set_point`}
                              type="text"
                              className="form-control text-right max-width-7"
                              id={`temperature_set_point-${index}`}
                              onBlur={(e: any) => ThermalOilHeaterFormik?.handleChange(e)}
                              defaultValue={ThermalOilHeaterFormik?.values?.thermalOilHeater[index]?.temperature_set_point}
                            />
                            <div className="input-group-text round_border">
                              <sup>o</sup>C
                            </div>
                          </div>
                          {ThermalOilHeaterFormik?.errors?.thermalOilHeater &&
                            ThermalOilHeaterFormik?.errors?.thermalOilHeater[index]
                              ?.temperature_set_point &&
                            env?.form_validation === true && !discardwarning && (
                              <ErrorTooltip
                                target={`temperature_set_point-${index}`}
                                message={
                                  ThermalOilHeaterFormik?.errors?.thermalOilHeater[index]
                                    ?.temperature_set_point
                                }
                                open={
                                  ThermalOilHeaterFormik?.errors?.thermalOilHeater &&
                                    ThermalOilHeaterFormik?.errors?.thermalOilHeater[index]
                                      ?.temperature_set_point
                                    ? true
                                    : false
                                }
                              />
                            )}
                        </Col>
                        <Col lg={3}>
                          <Label
                            className="mb-0"
                            for={`th_expansion_tank_level-${index}`}
                          >
                            TH expansion tank level
                          </Label>
                          <div className="input-group">
                            <Input
                              onFocus={(e) => e.target.select()}
                              name={`thermalOilHeater.${index}.th_expansion_tank_level`}
                              type="text"
                              className="form-control text-right max-width-7"
                              id={`th_expansion_tank_level-${index}`}
                              onBlur={(e: any) => ThermalOilHeaterFormik?.handleChange(e)}
                              defaultValue={ThermalOilHeaterFormik?.values?.thermalOilHeater[index]?.th_expansion_tank_level}
                            />
                            <div className="input-group-text round_border">
                              m
                            </div>
                          </div>
                          {ThermalOilHeaterFormik?.errors?.thermalOilHeater &&
                            ThermalOilHeaterFormik?.errors?.thermalOilHeater[index]
                              ?.th_expansion_tank_level &&
                            env?.form_validation === true && !discardwarning && (
                              <ErrorTooltip
                                target={`th_expansion_tank_level-${index}`}
                                message={
                                  ThermalOilHeaterFormik?.errors?.thermalOilHeater[index]
                                    ?.th_expansion_tank_level
                                }
                                open={
                                  ThermalOilHeaterFormik?.errors?.thermalOilHeater &&
                                    ThermalOilHeaterFormik?.errors?.thermalOilHeater[index]
                                      ?.th_expansion_tank_level
                                    ? true
                                    : false
                                }
                              />
                            )}
                        </Col>
                      </Row>
                      {index ===
                        ThermalOilHeaterFormik?.values?.thermalOilHeater?.length - 1 && (
                          <Label
                            className="link_color_blue pointer"
                            onClick={() => {
                              const data = [...ThermalOilHeaterFormik?.values?.thermalOilHeater];
                              data.push({
                                name_of_thermal_oil_heater: "",
                                outlet_temperature: 0,
                                inlet_temperature: 0,
                                exhaust_temperature: 0,
                                outlet_press: 0,
                                inlet_press: 0,
                                temperature_set_point: 0,
                                th_expansion_tank_level: 0,
                                vessel: VesselID,
                                voyage_information: VoyageID,
                                vessel_reporting_information: ReportID,
                              })
                              ThermalOilHeaterFormik?.setValues({
                                thermalOilHeater: data,
                              })
                            }}
                          >
                            Add another heater
                          </Label>
                        )}
                    </React.Fragment>
                  );
                }
              )}
            {popOverBool && deleteIndex !== null && (
              <DeletePopOver
                target={`delete_${deleteIndex}`}
                state={popOverBool}
                setState={setPopOverBool}
                onClick={async () => {
                  await handleDeletion(deleteIndex);
                  setPopOverBool(false);
                }}
              />
            )}
          </CardBody>
          <CardFooter className="p-2 py-3">
            <Row className="d-flex align-items-center flex-row-reverse">
              <Col lg={10}>
                <div className="d-flex flex-wrap gap-3 justify-content-end ">
                  {/* {editReportBool && (
                    <Button
                      type="reset"
                      color="danger"
                      className="btn_size_cstm"
                      onClick={() => {
                        setEditValues({
                          ...editValues,
                          deck_officer: {
                            ...editValues?.deck_officer,
                            lfonds_sludge_water_detail: editInitialValues?.deck_officer?.lfonds_sludge_water_detail,
                            lfonds_bilge_water_detail: editInitialValues?.deck_officer?.lfonds_bilge_water_detail,
                          }
                        });
                      }}
                    >
                      Reset
                    </Button>
                  )} */}
                  <Button
                    type={editReportBool ? "button" : "submit"}
                    color="primary"
                    className="btn_size_cstm"
                    disabled={ThermalOilHeaterFormik?.isSubmitting || editIsSubmitting}
                    onClick={async () => {
                      const validationErrors = await ThermalOilHeaterFormik?.validateForm();
                      Object.keys(ThermalOilHeaterFormik?.values || {}).forEach((key: any) => {
                        ThermalOilHeaterFormik?.setFieldTouched(key, true);
                      });
                      if (editReportBool && Object.keys(validationErrors).length === 0) {
                        await saveEditForm(editSubmit, toggleDynamicTabs, previousEnggTabIndex, setPreviousTabIndex);
                      }
                    }}
                  >
                    {(ThermalOilHeaterFormik?.isSubmitting || editIsSubmitting) && (
                      <i className="ms-2 spinner-border spinner-border-sm text-light me-2" />
                    )}
                    Next <i className="bx bx-chevron-right ms-1" />
                  </Button>
                </div>
              </Col>
              <Col lg={2}>
                <Button
                  type="button"
                  color="primary"
                  className="btn_size_cstm"
                  onClick={() => {
                    ThermalOilHeaterFormik?.setErrors({});
                    setErrorMessage(null);
                    if (activeAddTab === 1) {
                      toggleDynamicTabs(previousEnggTabIndex - 1);
                    } else {
                      toggleAddTab(activeAddTab - 1);
                    }
                  }}
                >
                  <i className="bx bx-chevron-left me-1" /> Previous
                </Button>
              </Col>
            </Row>
          </CardFooter>
          <FormValuesDebug
            values={[ThermalOilHeaterFormik?.values, ThermalOilHeaterFormik?.errors, ThermalOilHeaterFormik.initialValues]}
          />
        </Form>
      )}
    </Card>
  );
};

export default ThermalOilHeaterComponent;
